/**
 * Staff Timecard Models
 * Used for tracking clock-in/clock-out times
 */

export interface StaffTimecard {
  timecardID: number;
  userID: number;
  companyID: number;
  branchID: number;
  workDate: Date | string;
  clockInTime: Date | string;
  clockOutTime?: Date | string;
  totalHours?: number;
  notes?: string;
  status: string; // Active, Completed, Edited
  isManualEntry: boolean;

  // Multi clock-in/out support
  sessionNumber?: number;

  // Audit
  addedOn?: Date;
  addedBy?: number;
  updatedOn?: Date;
  updatedBy?: number;

  // Joined properties
  firstName?: string;
  lastName?: string;
  staffName?: string;
  branchName?: string;

  // Schedule info
  scheduledStartTime?: string;
  scheduledEndTime?: string;

  // Pagination
  totalCount?: number;
}

export interface StaffTimecardStatus {
  timecardID?: number;
  userID: number;
  companyID: number;
  branchID: number;
  workDate: Date | string;
  clockInTime?: Date | string;
  clockOutTime?: Date | string;
  totalHours?: number;
  status?: string;
  notes?: string;
  clockStatus: 'NotClockedIn' | 'ClockedIn' | 'ClockedOut';
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  // Multi clock-in/out support
  sessionNumber?: number;
  totalSessions?: number;
  totalDayHours?: number;
}

export interface ClockOperationResult {
  timecardID?: number;
  result: 'Success' | 'AlreadyClockedIn' | 'NotClockedIn' | 'AlreadyExists';
  clockInTime?: Date | string;
  clockOutTime?: Date | string;
  totalHours?: number;
  sessionNumber?: number;
}

export interface StaffTimecardDailyView {
  userID: number;
  firstName: string;
  lastName: string;
  staffName: string;
  branchID: number;
  branchName: string;

  // Schedule info
  scheduleID?: number;
  scheduledStartTime?: string;
  scheduledEndTime?: string;

  // Timecard info
  timecardID?: number;
  clockInTime?: Date | string;
  clockOutTime?: Date | string;
  totalHours?: number;
  timecardStatus?: string;
  notes?: string;
  isManualEntry?: boolean;

  // Multi clock-in/out support
  sessionNumber?: number;
  totalSessions?: number;
  totalDayHours?: number;
  hasActiveSession?: boolean; // True if user has any unclosed session today

  clockStatus: 'NotClockedIn' | 'ClockedIn' | 'ClockedOut';
  workDate: Date | string;
}

// Request params
export interface GetStaffTimecardParams {
  timecardID?: number;
  userID?: number;
  companyID?: number;
  branchID?: number;
  fromDate?: Date | string;
  toDate?: Date | string;
  status?: string;
  offSetRows?: number;
  nextRows?: number;
  sortExpression?: string;
}

export interface GetTodayStatusParams {
  userID: number;
  branchID: number;
  workDate?: Date | string;
}

export interface GetDailyViewParams {
  companyID: number;
  branchID?: number;
  workDate: Date | string;
}

export interface ClockInParams {
  userID: number;
  companyID: number;
  branchID: number;
  workDate?: string;      // Local date from frontend (YYYY-MM-DD)
  clockInTime?: string;   // Local datetime from frontend
  notes?: string;
}

export interface ClockOutParams {
  userID: number;
  branchID: number;
  workDate?: string;      // Local date from frontend (YYYY-MM-DD)
  clockOutTime?: string;  // Local datetime from frontend
  notes?: string;
}

export interface SelfClockInRequest {
  companyID: number;
  branchID: number;
  workDate?: string;      // Local date from frontend (YYYY-MM-DD)
  clockInTime?: string;   // Local datetime from frontend
  notes?: string;
}

export interface SelfClockOutRequest {
  branchID: number;
  workDate?: string;      // Local date from frontend (YYYY-MM-DD)
  clockOutTime?: string;  // Local datetime from frontend
  notes?: string;
}

export interface InsertStaffTimecardParams {
  userID: number;
  companyID: number;
  branchID: number;
  workDate: Date | string;
  clockInTime: Date | string;
  clockOutTime?: Date | string;
  notes?: string;
  // Optional: Override scheduled times for historical entries
  scheduledStartTime?: string;  // Format: "HH:mm:ss"
  scheduledEndTime?: string;    // Format: "HH:mm:ss"
}

export interface UpdateStaffTimecardParams {
  timecardID: number;
  clockInTime: Date | string;
  clockOutTime?: Date | string;
  notes?: string;
}

// Helper functions
export function formatTimeFromString(timeString: string | null | undefined): string {
  if (!timeString) return '-';

  // Handle "HH:mm:ss" format from TimeSpan
  if (timeString.includes(':')) {
    const parts = timeString.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes}${ampm}`;
  }
  return timeString;
}

export function formatDateTimeToTime(dateTime: Date | string | null | undefined): string {
  if (!dateTime) return '-';

  const date = new Date(dateTime);
  if (isNaN(date.getTime())) return '-';

  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes}${ampm}`;
}

export function calculateHoursWorked(clockIn: Date | string, clockOut: Date | string): number {
  const inTime = new Date(clockIn).getTime();
  const outTime = new Date(clockOut).getTime();
  const diffMs = outTime - inTime;
  return Math.round((diffMs / (1000 * 60 * 60)) * 100) / 100;
}

export function getClockStatusClass(status: string): string {
  switch (status) {
    case 'ClockedIn':
      return 'badge badge-success';
    case 'ClockedOut':
      return 'badge badge-secondary';
    case 'NotClockedIn':
    default:
      return 'badge badge-light';
  }
}

export function getClockStatusLabel(status: string): string {
  switch (status) {
    case 'ClockedIn':
      return 'Working';
    case 'ClockedOut':
      return 'Completed';
    case 'NotClockedIn':
    default:
      return 'Not Started';
  }
}
